import React, { useCallback, useEffect, useState } from 'react';
import {
  createFeeCharge,
  deleteFeeCharge,
  fetchFeeCharges,
  type FeeChargeDto,
} from '../api/axios';
import styles from './FeeManagement.module.css';
import { classLevelWord, formatClassLevelLabel, getMaxClassLevel } from '../utils/classLevelLabel';

interface Props {
  school: string;
  academicYear: string;
  term: number;
}

export const FeeChargesPanel: React.FC<Props> = ({ school, academicYear, term }) => {
  const [charges, setCharges] = useState<FeeChargeDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [targetScope, setTargetScope] = useState<'single' | 'multiple' | 'all'>('all');
  const [form, setForm] = useState(1);
  const [selectedForms, setSelectedForms] = useState<number[]>([]);
  const [boardingFilter, setBoardingFilter] = useState<'both' | 'boarder' | 'day_scholar'>('both');

  const maxClassLevel = getMaxClassLevel(school);
  const classLevels = Array.from({ length: maxClassLevel }, (_, i) => i + 1);

  const load = useCallback(async () => {
    if (!academicYear || !term) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchFeeCharges({
        school,
        academic_year: academicYear,
        term,
      });
      setCharges(data);
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { error?: unknown; message?: unknown } } })?.response?.data;
      const msg = data?.error || data?.message;
      setError(typeof msg === 'string' && msg !== '' ? msg : 'Failed to load specific charges');
    } finally {
      setLoading(false);
    }
  }, [academicYear, school, term]);

  useEffect(() => {
    void load();
  }, [load]);

  const whoPays = (charge: FeeChargeDto): string => {
    const boarding =
      charge.boarding_filter === 'boarder'
        ? 'Boarders'
        : charge.boarding_filter === 'day_scholar'
          ? 'Day scholars'
          : 'Boarders and day scholars';
    if (charge.target_scope === 'all' || !charge.forms?.length) {
      return `All classes · ${boarding}`;
    }
    const labels = charge.forms.map((level) => formatClassLevelLabel(school, level)).join(', ');
    return `${labels} · ${boarding}`;
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    if (!title.trim()) {
      setError('Enter what the charge is for');
      return;
    }
    if (!amount || Number(amount) < 0) {
      setError('Enter a valid amount');
      return;
    }
    if (targetScope === 'multiple' && selectedForms.length === 0) {
      setError('Select at least one class');
      return;
    }

    setSaving(true);
    try {
      const result = await createFeeCharge({
        school,
        academic_year: academicYear,
        term,
        title: title.trim(),
        amount: Number(amount),
        description: description.trim() || undefined,
        target_scope: targetScope,
        form: targetScope === 'single' ? form : undefined,
        forms: targetScope === 'multiple' ? selectedForms : undefined,
        boarding_filter: boardingFilter,
      });
      setSuccess(`${result.charge.title} added and applied to ${result.students_updated} fee account(s).`);
      setTitle('');
      setAmount('');
      setDescription('');
      setOpen(false);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.response?.data?.message || 'Failed to add charge');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (charge: FeeChargeDto) => {
    const confirmed = window.confirm(
      `Remove “${charge.title}” from Term ${charge.term} ${charge.academic_year}? Matching balances will be recalculated.`
    );
    if (!confirmed) return;
    setError(null);
    setSuccess(null);
    try {
      await deleteFeeCharge(charge.id);
      setSuccess('Charge removed and balances recalculated.');
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to remove charge');
    }
  };

  return (
    <div style={{ marginTop: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 12 }}>
        <h3 className={styles.sectionTitle} style={{ marginBottom: 0 }}>
          Specific charges · Term {term} {academicYear}
        </h3>
        <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => setOpen(true)}>
          Add a specific charge
        </button>
      </div>
      <p style={{ color: '#6b7280', marginTop: 0, marginBottom: 12 }}>
        A charge is added to the students who match the class and boarding selection for this term only.
      </p>
      {error && <div className={`${styles.alert} ${styles.alertError}`}>{error}</div>}
      {success && <div className={`${styles.alert} ${styles.alertSuccess}`}>{success}</div>}
      {loading ? (
        <p>Loading charges...</p>
      ) : charges.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No specific charges for this term.</p>
        </div>
      ) : (
        <div className="tableScroll">
          <table className={styles.table}>
            <thead>
              <tr>
                <th>What it is for</th>
                <th>Who pays</th>
                <th>Amount (MK)</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {charges.map((charge) => (
                <tr key={charge.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{charge.title}</div>
                    {charge.description && <div style={{ color: '#6b7280', fontSize: 13 }}>{charge.description}</div>}
                  </td>
                  <td>{whoPays(charge)}</td>
                  <td>{Number(charge.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  <td>
                    <button className={`${styles.btn} ${styles.btnDanger}`} onClick={() => void handleDelete(charge)}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalCard}>
            <div className={styles.modalHeader}>
              <h4 className={styles.modalTitle}>Add a specific charge</h4>
            </div>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>What it is for</label>
                <input className={styles.formInput} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Sports day" />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Amount (MK)</label>
                <input className={styles.formInput} type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Who pays</label>
                <select className={styles.formSelect} value={targetScope} onChange={(e) => setTargetScope(e.target.value as 'single' | 'multiple' | 'all')}>
                  <option value="all">All classes</option>
                  <option value="single">One class</option>
                  <option value="multiple">Several classes</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Boarding</label>
                <select className={styles.formSelect} value={boardingFilter} onChange={(e) => setBoardingFilter(e.target.value as 'both' | 'boarder' | 'day_scholar')}>
                  <option value="both">Boarders and day scholars</option>
                  <option value="boarder">Boarders only</option>
                  <option value="day_scholar">Day scholars only</option>
                </select>
              </div>
              {targetScope === 'single' && (
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{classLevelWord(school)}</label>
                  <select className={styles.formSelect} value={form} onChange={(e) => setForm(Number(e.target.value))}>
                    {classLevels.map((level) => (
                      <option key={level} value={level}>{formatClassLevelLabel(school, level)}</option>
                    ))}
                  </select>
                </div>
              )}
              {targetScope === 'multiple' && (
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label className={styles.formLabel}>{classLevelWord(school)}s</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {classLevels.map((level) => (
                      <label key={level} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selectedForms.includes(level)}
                          onChange={(e) => {
                            setSelectedForms((prev) =>
                              e.target.checked ? [...prev, level] : prev.filter((item) => item !== level)
                            );
                          }}
                        />
                        {formatClassLevelLabel(school, level)}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                <label className={styles.formLabel}>Note (optional)</label>
                <textarea className={styles.formTextarea} value={description} onChange={(e) => setDescription(e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setOpen(false)}>Cancel</button>
              <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving...' : 'Save charge'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
